import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
// @tiptap/extension-text-style@3.22.x bundles all the run-level mark
// extensions as named exports: TextStyle (the foundation mark), Color,
// FontSize, FontFamily, BackgroundColor, LineHeight. Using these
// upstream-maintained extensions is cleaner than the custom RunStyle
// hack — they handle parseHTML/renderHTML round-tripping for us.
import {
  TextStyle,
  Color,
  FontSize,
  FontFamily,
  BackgroundColor,
  LineHeight,
} from '@tiptap/extension-text-style';
// Table support — without these, mammoth-imported <table> elements get
// flattened to vertical-stacked paragraphs in the editor (the visible
// breakage on the imported Mediation Confidentiality Agreement signature
// row). Table needs all four extensions to work.
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import Link from '@tiptap/extension-link';
import { Extension } from '@tiptap/core';
import CustomImage, { IMAGE_WIDTH_PRESETS } from '../components/email/CustomImageExtension';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  MergeTagNode,
  htmlToEditorContent,
  editorContentToHtml,
} from '../components/email/MergeTagExtension';
import { DEFAULT_AGREEMENT_TEMPLATE_HTML } from '../components/email/defaultAgreementTemplate';
import { useResizableColumns, ResizeHandle, ColumnDivider, useSortableData, SortableHeader } from '../components/shared/tableUtils';

// Preserve a small whitelist of inline CSS properties on paragraphs +
// headings so DOCX-imported content keeps its line-height / indentation /
// text-indent. TipTap's StarterKit Paragraph/Heading nodes drop unknown
// `style` attributes by default. This extension adds a single global
// `style` attribute back, but ONLY copies whitelisted properties from the
// source — nothing else makes it through (so we don't accidentally
// import malicious CSS or break the editor's own styling).
const PRESERVED_PARA_PROPS = ['line-height', 'padding-left', 'text-indent'];
const InlineStyleExtension = Extension.create({
  name: 'inlineStyle',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          inlineStyle: {
            default: null,
            parseHTML: (element) => {
              const raw = element.getAttribute('style') || '';
              if (!raw) return null;
              const kept = raw
                .split(';')
                .map((s) => s.trim())
                .filter(Boolean)
                .filter((decl) => {
                  const prop = decl.split(':')[0]?.trim().toLowerCase();
                  return PRESERVED_PARA_PROPS.includes(prop);
                })
                .join('; ');
              return kept || null;
            },
            renderHTML: (attrs) => {
              if (!attrs.inlineStyle) return {};
              return { style: attrs.inlineStyle };
            },
          },
        },
      },
    ];
  },
});

// Run-level marks (font size, family, color, background, line-height)
// come from `@tiptap/extension-text-style` via named imports above —
// no custom extension needed.

// Merge tags available in the agreement template editor. Mirror the keys
// that signingController.createSigningRequest substitutes at send time.
// Order matters: tags listed first appear at the top of the sidebar pill
// list. Mediator Name is intentionally first since it's the most common
// per-case substitution and the dynamic-label preview keys off it.
/* ── HTML cleanup for editor content ─────────────────────────────────── */
// stripTrailingEmptyTables: drop hollow placeholder grids from the end of
// the HTML. A table counts as empty when every row except the first
// (treated as a possible header) has no real content. Same logic as the
// signing-page renderer so the editor view matches what signers see.
function stripTrailingEmptyTables(html) {
  if (!html || typeof window === 'undefined') return html || '';
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  const tableIsEmpty = (table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length === 0) return true;
    const dataRows = rows.length > 1 ? rows.slice(1) : rows;
    return dataRows.every((row) => {
      const cells = Array.from(row.querySelectorAll('td, th'));
      if (cells.length === 0) return true;
      return cells.every((cell) => {
        const text = (cell.textContent || '').trim();
        const hasMedia = cell.querySelector('img, svg, canvas, video, input');
        return !text && !hasMedia;
      });
    });
  };
  const isBlank = (node) => {
    if (!node) return false;
    if (node.nodeType === 3) return !node.textContent.trim();
    if (node.nodeType !== 1) return false;
    const tag = node.tagName.toLowerCase();
    if (tag === 'br') return true;
    if (tag === 'table') return tableIsEmpty(node);
    if (['p', 'div', 'span'].includes(tag)) {
      const hasText = (node.textContent || '').trim().length > 0;
      const hasMedia = node.querySelector('img, svg, canvas, video, input, hr');
      return !hasText && !hasMedia;
    }
    return false;
  };
  while (wrapper.lastChild && isBlank(wrapper.lastChild)) {
    wrapper.removeChild(wrapper.lastChild);
  }
  return wrapper.innerHTML;
}

/* Apply a list the way Google Docs does: one marker per visible line.
 *
 * This mirrors the email-template editor's list flow (components/email-editor/
 * EditorToolbar.jsx) so the two behave identically,
 * with two differences called out below. Nothing is imported from there —
 * the agreement editor keeps its own copy so either can change without
 * touching the other.
 *
 * 1. Hard breaks become real paragraphs.
 *    A list marker belongs to a BLOCK: ProseMirror emits one <li> per block,
 *    and a hard break (Shift+Enter, or a Word soft line break from the DOCX
 *    import) is inline. So three lines written as
 *
 *        <p>name.<br>ammm.<br>sssss.</p>
 *
 *    are ONE block and collapse into ONE <li>. The email editor solves this
 *    at LOAD time (htmlConversion.js splits <br> into <p> before the content
 *    reaches TipTap). That exact approach can't be copied here: email
 *    paragraphs are `margin: 0`, so splitting is invisible, whereas agreement
 *    paragraphs are `margin: 12px 0` — splitting on load would silently add
 *    24px between every previously-tight line of every existing agreement.
 *    Doing it at toggle time instead reaches the same result for the user
 *    while leaving documents nobody is editing exactly as they are.
 *
 *    Edits are applied back-to-front so positions collected up front stay
 *    valid as the document shifts, and the selection is re-anchored across
 *    the new paragraphs so the toggle still covers all of them.
 *
 * 2. A selection that bleeds into the next block is trimmed.
 *    Dragging past the end of a line usually lands at offset 0 of the block
 *    below, which would pull an unwanted extra paragraph into the list.
 *    Straight port of the email editor's guard.
 *
 * Callers must use onMouseDown with preventDefault(), not onClick — a click
 * blurs the canvas first and the selection is gone before the handler runs.
 * That is also how the email toolbar wires its list buttons.
 */
const applyList = (editor, toggleCommand) => {
  const { state } = editor
  const { from, to } = state.selection
  const breaks = []
  state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === 'hardBreak') breaks.push(pos)
  })

  // Shrink a selection that spills into the start of the following block.
  const trimmed = (docFrom, docTo, doc) => {
      if (docFrom === docTo || docTo <= docFrom) return docTo
      return doc.resolve(docTo).parentOffset === 0 ? docTo - 1 : docTo
  }

  if (breaks.length) {
      const tr = state.tr
      for (const pos of breaks.reverse()) {
      const at = tr.mapping.map(pos)
      tr.delete(at, at + 1)
      tr.split(at)
      }
      const nextFrom = tr.mapping.map(from)
      const nextTo = tr.mapping.map(to)
      editor.view.dispatch(tr)
      const doc = editor.state.doc
      editor
      .chain()
      .focus()
      .setTextSelection({ from: nextFrom, to: trimmed(nextFrom, nextTo, doc) })
      [toggleCommand]()
      .run()
      return
  }

  const end = trimmed(from, to, state.doc)
  if (end !== to) {
      editor.chain().focus().setTextSelection({ from, to: end })[toggleCommand]().run()
      return
  }
  editor.chain().focus()[toggleCommand]().run()
}

const MERGE_TAGS = [
  { label: 'Mediator Name', tag: '{{mediator_name}}' },
  { label: 'Case Name', tag: '{{case_name}}' },
  { label: 'Plaintiff Name', tag: '{{plaintiff_name}}' },
  { label: 'Defendant Name', tag: '{{defendant_name}}' },
  { label: 'Mediation Date', tag: '{{mediation_date}}' },
  // Slot + start time, e.g. "Morning 9:00 AM". Sourced from cases.slot_type
  // and cases.case_schedule_time, so it stays correct when a case is
  // rescheduled before the agreement goes out.
  { label: 'Mediation Time', tag: '{{mediation_time}}' },
  // cases.venue_address for in-person mediations; virtual cases render
  // "Virtual (Zoom)" rather than an empty line.
  { label: 'Location', tag: '{{location}}' },
  // The COURT case number the mediator typed on the case (cases.case_number,
  // e.g. "24STCV13426") — not the internal cases.id, which is {{case_id}}.
  { label: 'Case Number', tag: '{{case_number}}' },
  // 'Date Signed' removed — it doesn't make sense as an authoring-time
  // merge tag. The signing date is captured per-signer at sign-time and
  // belongs on the certificate/audit row, not baked into the template
  // body. Each signer's date already appears on their signature row in
  // the rendered agreement and the signing certificate.
];

/* ── Inline dialogs / toasts ─────────────────────────────────────────── */
// Replaces window.alert / window.prompt / window.confirm so the editor
// stays in a polished modal world instead of jumping into native browser
// dialogs (which are jarring during a live demo).

function Toast({ kind, message, onDismiss }) {
  React.useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [message, onDismiss]);
  if (!message) return null;
  const styles = kind === 'error'
    ? 'bg-red-50 border-red-200 text-red-700'
    : 'bg-green-50 border-green-200 text-green-700';
  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-5 right-5 z-[60] max-w-sm border rounded-lg shadow-md px-4 py-3 text-[13px] ${styles}`}
      style={{ fontFamily: 'Inter' }}
    >
      <div className="flex items-start gap-3">
        <span className="flex-1">{message}</span>
        <button
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="opacity-60 hover:opacity-100 transition"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Tiny prompt modal used for "Insert Link". Resolves on submit/cancel.
function PromptDialog({ open, title, label, placeholder, defaultValue, onSubmit, onCancel }) {
  const [value, setValue] = React.useState(defaultValue || '');
  React.useEffect(() => { if (open) setValue(defaultValue || ''); }, [open, defaultValue]);
  if (!open) return null;
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(value.trim()); };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onMouseDown={onCancel}>
      <form
        onSubmit={handleSubmit}
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-5"
        style={{ fontFamily: 'Inter' }}
      >
        <div className="text-sm font-semibold text-[#0e1e20] mb-1" style={{ fontFamily: 'Inter Tight' }}>
          {title}
        </div>
        {label && <div className="text-[12px] text-[#5b5a64] mb-3">{label}</div>}
        <input
          autoFocus
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="w-full h-10 px-3 rounded-lg border border-[#e5e5e5] text-[13px] outline-none focus:border-[#115e59] focus:ring-2 focus:ring-[#115e59]/15"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-8 px-4 rounded-lg border border-[#e5e5e5] text-[13px] font-medium text-[#5b5a64] hover:bg-gray-50"
          >Cancel</button>
          <button
            type="submit"
            disabled={!value.trim()}
            className="h-8 px-4 rounded-lg bg-[#115e59] text-white text-[13px] font-medium hover:bg-[#0d4f4a] disabled:opacity-50"
          >OK</button>
        </div>
      </form>
    </div>
  );
}

// Confirm modal — replaces window.confirm. Destructive variant uses red
// for the primary button (Delete Template).
function ConfirmDialog({ open, title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onMouseDown={onCancel}>
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-5"
        style={{ fontFamily: 'Inter' }}
      >
        <div className="text-sm font-semibold text-[#0e1e20] mb-1" style={{ fontFamily: 'Inter Tight' }}>{title}</div>
        {body && <div className="text-[13px] text-[#5b5a64] leading-relaxed">{body}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-8 px-4 rounded-lg border border-[#e5e5e5] text-[13px] font-medium text-[#5b5a64] hover:bg-gray-50"
          >{cancelLabel}</button>
          <button
            type="button"
            onClick={onConfirm}
            className={`h-8 px-4 rounded-lg text-white text-[13px] font-medium ${
              destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-[#115e59] hover:bg-[#0d4f4a]'
            }`}
          >{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Toolbar config ──────────────────────────────────────────────────── */
// Word-style font family options. Web-safe stack on the right matches
// what the same fontFamily resolves to when the doc renders outside the
// editor (sign page, email PDF render).
const FONT_FAMILIES = [
  { label: 'Default',         value: '' },
  { label: 'Arial',           value: 'Arial, Helvetica, sans-serif' },
  { label: 'Calibri',         value: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' },
  { label: 'Cambria',         value: 'Cambria, Georgia, serif' },
  { label: 'Comic Sans MS',   value: '"Comic Sans MS", "Chalkboard SE", cursive' },
  { label: 'Courier New',     value: '"Courier New", Courier, monospace' },
  { label: 'Georgia',         value: 'Georgia, serif' },
  { label: 'Helvetica',       value: 'Helvetica, Arial, sans-serif' },
  { label: 'Inter',           value: 'Inter, sans-serif' },
  { label: 'Roboto',          value: 'Roboto, "Helvetica Neue", Arial, sans-serif' },
  { label: 'Tahoma',          value: 'Tahoma, Geneva, sans-serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Trebuchet MS',    value: '"Trebuchet MS", "Lucida Sans Unicode", sans-serif' },
  { label: 'Verdana',         value: 'Verdana, Geneva, sans-serif' },
];
const FONT_SIZES = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48', '60', '72'];
const LINE_HEIGHTS = [
  { label: 'Single',  value: '1.0' },
  { label: '1.15',    value: '1.15' },
  { label: '1.5',     value: '1.5' },
  { label: 'Double',  value: '2.0' },
  { label: '2.5',     value: '2.5' },
  { label: 'Triple',  value: '3.0' },
];
// Word's default text/highlight color palettes. Order matches Word for
// muscle memory.
const TEXT_COLORS = [
  '#000000', '#404040', '#7F7F7F', '#BFBFBF', '#FFFFFF',
  '#C00000', '#FF0000', '#FFC000', '#FFFF00', '#92D050', '#00B050',
  '#00B0F0', '#0070C0', '#002060', '#7030A0',
  '#115E59', '#0d4f4a',
];
const HIGHLIGHT_COLORS = [
  null,        // (none)
  '#FFFF00', '#00FFFF', '#00FF00', '#FF00FF', '#0000FF', '#FF0000',
  '#000080', '#008080', '#008000', '#800080', '#800000', '#808000',
  '#808080', '#C0C0C0',
];

/* ── Toolbar dropdown helper ─────────────────────────────────────────── */
// Custom dropdown matching the rest of the app — never a native <select>.
// Trigger button shows the current label + chevron; click toggles a
// popover with the option list. Closes on outside-click via the parent
// toolbar's shared close handler (passed in as `onRequestClose`).
function ToolbarDropdown({
  open, onToggle, onClose, label, width = 160, align = 'left', children,
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className="h-8 inline-flex items-center gap-1 px-2 rounded text-xs text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors [&_*]:pointer-events-none"
      >
        <span className="truncate max-w-[120px]">{label}</span>
        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className={`absolute top-full mt-1 z-30 bg-white border border-gray-200 rounded-md shadow-lg max-h-[280px] overflow-auto ${align === 'right' ? 'right-0' : 'left-0'}`}
          style={{ width }}
        >
          {children(onClose)}
        </div>
      )}
    </div>
  );
}

/* ── Toolbar ─────────────────────────────────────────────────────────── */
function Toolbar({ editor, onInsertImage }) {
  const [textColorOpen, setTextColorOpen] = React.useState(false);
  const [highlightOpen, setHighlightOpen] = React.useState(false);
  const [paraOpen, setParaOpen] = React.useState(false);
  const [fontFamilyOpen, setFontFamilyOpen] = React.useState(false);
  const [fontSizeOpen, setFontSizeOpen] = React.useState(false);
  const [lineHeightOpen, setLineHeightOpen] = React.useState(false);
  const [linkPromptOpen, setLinkPromptOpen] = React.useState(false);
  const toolbarRef = React.useRef(null);

  // Force this toolbar to re-render whenever the editor's selection
  // moves or its content changes. Without this, `editor.isActive(...)`
  // returns stale values: e.g. the image-active highlight stays on
  // after the image is deleted, and the table-context Delete Table
  // button never appears because React never re-runs the render.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!editor) return undefined;
    const bump = () => setTick((t) => t + 1);
    editor.on('selectionUpdate', bump);
    editor.on('transaction', bump);
    return () => {
      editor.off('selectionUpdate', bump);
      editor.off('transaction', bump);
    };
  }, [editor]);

  // Close every popover when the user clicks outside the toolbar. One
  // listener handles all six dropdowns / pickers.
  React.useEffect(() => {
    function onDocClick(e) {
      if (!toolbarRef.current?.contains(e.target)) {
        setTextColorOpen(false);
        setHighlightOpen(false);
        setParaOpen(false);
        setFontFamilyOpen(false);
        setFontSizeOpen(false);
        setLineHeightOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // When opening any dropdown, close every other one — only one popover
  // open at a time so they don't overlap.
  const closeAll = () => {
    setTextColorOpen(false);
    setHighlightOpen(false);
    setParaOpen(false);
    setFontFamilyOpen(false);
    setFontSizeOpen(false);
    setLineHeightOpen(false);
  };

  if (!editor) return null;

  // Toolbar button. Two non-obvious-but-critical things:
  //   • onMouseDown.preventDefault() stops focus from leaving the
  //     editor when the user mousedowns on the button. Without this,
  //     the editor's selection collapses to nothing on the first
  //     click and the command runs against an empty selection — so
  //     it appears to "do nothing" the first time and only work on
  //     the second click (when the selection is re-established).
  //   • [&_*]:pointer-events-none on children means clicks on inner
  //     SVG <path>/<svg> always target the button itself, not an
  //     inner element. Otherwise the inner path can swallow the
  //     click and Chrome registers it as a different target than
  //     the button.
  const B = ({ active, onClick, children, title, disabled }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`w-8 h-8 flex items-center justify-center rounded text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed [&_*]:pointer-events-none ${
        active ? 'bg-teal-100 text-teal-800' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );

  // Read the current run's font size / family / line-height so the
  // dropdowns reflect the cursor selection.
  const currentFontFamily = editor.getAttributes('textStyle').fontFamily || '';
  const currentFontSize = (() => {
    const v = editor.getAttributes('textStyle').fontSize || '';
    // Strip 'px' so the dropdown matches.
    return v.replace(/px$/, '');
  })();
  const currentColor = editor.getAttributes('textStyle').color || '';
  const currentHighlight = editor.getAttributes('textStyle').backgroundColor || '';
  // LineHeight extension stores on the paragraph/heading node attribute
  // (we configured types: ['paragraph','heading'] below). Heading takes
  // priority when the cursor is on one.
  const currentLineHeight =
    editor.getAttributes('heading').lineHeight ||
    editor.getAttributes('paragraph').lineHeight ||
    '';

  // Headings dropdown value.
  const paraValue = editor.isActive('heading')
    ? `h${[1, 2, 3, 4].find((l) => editor.isActive('heading', { level: l })) || 1}`
    : 'p';

  return (
    <div
      ref={toolbarRef}
      onMouseDown={(e) => {
        // Wrapper-level guard: prevents the editor from losing its
        // selection when the user mousedowns ANYWHERE in the
        // toolbar — covers raw <button>s, dropdown options, color
        // swatches, etc. without having to patch every callsite.
        // Buttons that need to claim focus (e.g. inputs) can override
        // by stopping propagation.
        if (e.target.closest('input, textarea, [contenteditable]')) return;
        e.preventDefault();
      }}
      className="flex items-center gap-0.5 px-3 py-1.5 border-b border-gray-100 flex-wrap"
    >
      {/* ── Undo / Redo ───────────────────────────────────────────── */}
      <B
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (⌘Z)"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v0a5 5 0 01-5 5H7m-4-10l4-4m-4 4l4 4" />
        </svg>
      </B>
      <B
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (⌘⇧Z)"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v0a5 5 0 005 5h6m4-10l-4-4m4 4l-4 4" />
        </svg>
      </B>
      <div className="w-px h-5 bg-gray-200 mx-1" />

      {/* ── Paragraph style ──────────────────────────────────────── */}
      <ToolbarDropdown
        open={paraOpen}
        onToggle={() => { closeAll(); setParaOpen((v) => !v); }}
        onClose={() => setParaOpen(false)}
        label={
          paraValue === 'p' ? 'Body Text' :
          paraValue === 'h1' ? 'Heading 1' :
          paraValue === 'h2' ? 'Heading 2' :
          paraValue === 'h3' ? 'Heading 3' :
          'Heading 4'
        }
        width={150}
      >
        {(close) => (
          <>
            {[
              { val: 'p',  label: 'Body Text', sample: <span className="text-[13px]">Body Text</span> },
              { val: 'h1', label: 'Heading 1', sample: <span className="text-[18px] font-bold">Heading 1</span> },
              { val: 'h2', label: 'Heading 2', sample: <span className="text-[16px] font-semibold">Heading 2</span> },
              { val: 'h3', label: 'Heading 3', sample: <span className="text-[14px] font-semibold">Heading 3</span> },
              { val: 'h4', label: 'Heading 4', sample: <span className="text-[13px] font-semibold">Heading 4</span> },
            ].map((opt) => (
              <button
                key={opt.val}
                type="button"
                onClick={() => {
                  if (opt.val === 'p') editor.chain().focus().setParagraph().run();
                  else editor.chain().focus().toggleHeading({ level: parseInt(opt.val[1]) }).run();
                  close();
                }}
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${paraValue === opt.val ? 'bg-teal-50 text-teal-700' : 'text-gray-700'}`}
              >
                {opt.sample}
              </button>
            ))}
          </>
        )}
      </ToolbarDropdown>
      <div className="w-1" />

      {/* ── Font family ──────────────────────────────────────────── */}
      <ToolbarDropdown
        open={fontFamilyOpen}
        onToggle={() => { closeAll(); setFontFamilyOpen((v) => !v); }}
        onClose={() => setFontFamilyOpen(false)}
        label={FONT_FAMILIES.find((f) => f.value === currentFontFamily)?.label || 'Default'}
        width={180}
      >
        {(close) => (
          <>
            {FONT_FAMILIES.map((f) => (
              <button
                key={f.label}
                type="button"
                onClick={() => {
                  if (!f.value) editor.chain().focus().unsetFontFamily?.().run();
                  else editor.chain().focus().setFontFamily?.(f.value).run();
                  close();
                }}
                className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 transition-colors ${currentFontFamily === f.value ? 'bg-teal-50 text-teal-700' : 'text-gray-700'}`}
                style={{ fontFamily: f.value || 'inherit' }}
              >
                {f.label}
              </button>
            ))}
          </>
        )}
      </ToolbarDropdown>
      <div className="w-1" />

      {/* ── Font size ────────────────────────────────────────────── */}
      <ToolbarDropdown
        open={fontSizeOpen}
        onToggle={() => { closeAll(); setFontSizeOpen((v) => !v); }}
        onClose={() => setFontSizeOpen(false)}
        label={currentFontSize || '12'}
        width={70}
      >
        {(close) => (
          <>
            <button
              type="button"
              onClick={() => { editor.chain().focus().unsetFontSize?.().run(); close(); }}
              className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 transition-colors ${!currentFontSize ? 'bg-teal-50 text-teal-700' : 'text-gray-500'}`}
            >
              Default
            </button>
            {FONT_SIZES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { editor.chain().focus().setFontSize?.(`${s}px`).run(); close(); }}
                className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 transition-colors ${currentFontSize === s ? 'bg-teal-50 text-teal-700' : 'text-gray-700'}`}
              >
                {s}
              </button>
            ))}
          </>
        )}
      </ToolbarDropdown>
      <div className="w-1" />

      {/* ── B / I / U / Strike ───────────────────────────────────── */}
      <B active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (⌘B)">
        <span className="font-bold">B</span>
      </B>
      <B active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (⌘I)">
        <span className="italic">I</span>
      </B>
      <B active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline (⌘U)">
        <span className="underline">U</span>
      </B>
      <B active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
        <span className="line-through">S</span>
      </B>

      {/* ── Text color ───────────────────────────────────────────── */}
      <div className="relative">
        <B
          active={!!currentColor}
          onClick={(e) => { e.stopPropagation?.(); closeAll(); setTextColorOpen((v) => !v); }}
          title="Font color"
        >
          <span className="flex flex-col items-center leading-none">
            <span className="text-[12px] font-bold">A</span>
            <span className="block w-4 h-[3px] mt-[1px]" style={{ backgroundColor: currentColor || '#000' }} />
          </span>
        </B>
        {textColorOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute top-full left-0 mt-1 z-30 bg-white border border-gray-200 rounded-md shadow-lg p-2 w-[148px]"
          >
            <button
              type="button"
              onClick={() => { editor.chain().focus().unsetColor?.().run(); setTextColorOpen(false); }}
              className="w-full text-[11px] text-gray-500 mb-1 hover:text-gray-700"
            >
              Reset color
            </button>
            <div className="grid grid-cols-6 gap-1">
              {TEXT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { editor.chain().focus().setColor?.(c).run(); setTextColorOpen(false); }}
                  className="w-5 h-5 rounded border border-gray-300"
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Highlight color ──────────────────────────────────────── */}
      <div className="relative">
        <B
          active={!!currentHighlight}
          onClick={(e) => { e.stopPropagation?.(); closeAll(); setHighlightOpen((v) => !v); }}
          title="Highlight color"
        >
          <span className="flex flex-col items-center leading-none">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11l3 3 8-8-3-3-8 8z" />
              <path d="M5 15l4 4-2 2H3v-4l2-2z" />
            </svg>
            <span className="block w-4 h-[3px] mt-[1px]" style={{ backgroundColor: currentHighlight || '#FFFF00' }} />
          </span>
        </B>
        {highlightOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute top-full left-0 mt-1 z-30 bg-white border border-gray-200 rounded-md shadow-lg p-2 w-[148px]"
          >
            <button
              type="button"
              onClick={() => { editor.chain().focus().unsetBackgroundColor?.().run(); setHighlightOpen(false); }}
              className="w-full text-[11px] text-gray-500 mb-1 hover:text-gray-700"
            >
              No highlight
            </button>
            <div className="grid grid-cols-6 gap-1">
              {HIGHLIGHT_COLORS.filter(Boolean).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { editor.chain().focus().setBackgroundColor?.(c).run(); setHighlightOpen(false); }}
                  className="w-5 h-5 rounded border border-gray-300"
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Clear formatting ─────────────────────────────────────── */}
      <B
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        title="Clear formatting"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21l9-9m0 0L21 3M12 12l9 9M12 12L3 3" />
        </svg>
      </B>

      <div className="w-px h-5 bg-gray-200 mx-1" />

      {/* ── Alignment ────────────────────────────────────────────── */}
      <B active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align Left">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 5A.75.75 0 012.75 9h9.5a.75.75 0 010 1.5h-9.5A.75.75 0 012 9.75zm0 5a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" /></svg>
      </B>
      <B active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align Center">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm3 5A.75.75 0 015.75 9h8.5a.75.75 0 010 1.5h-8.5A.75.75 0 015 9.75zm-3 5a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" /></svg>
      </B>
      <B active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align Right">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm5 5A.75.75 0 017.75 9h9.5a.75.75 0 010 1.5h-9.5A.75.75 0 017 9.75zm-5 5a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" /></svg>
      </B>
      <B active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justify">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 5A.75.75 0 012.75 9h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 9.75zm0 5a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" /></svg>
      </B>

      {/* ── Line height ─────────────────────────────────────────── */}
      <ToolbarDropdown
        open={lineHeightOpen}
        onToggle={() => { closeAll(); setLineHeightOpen((v) => !v); }}
        onClose={() => setLineHeightOpen(false)}
        label={
          LINE_HEIGHTS.find((lh) => lh.value === currentLineHeight)?.label || 'Lines'
        }
        width={110}
      >
        {(close) => (
          <>
            <button
              type="button"
              onClick={() => { editor.chain().focus().unsetLineHeight?.().run?.(); close(); }}
              className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 transition-colors ${!currentLineHeight ? 'bg-teal-50 text-teal-700' : 'text-gray-500'}`}
            >
              Default
            </button>
            {LINE_HEIGHTS.map((lh) => (
              <button
                key={lh.value}
                type="button"
                onClick={() => { editor.chain().focus().setLineHeight?.(lh.value).run?.(); close(); }}
                className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 transition-colors ${currentLineHeight === lh.value ? 'bg-teal-50 text-teal-700' : 'text-gray-700'}`}
              >
                {lh.label}
              </button>
            ))}
          </>
        )}
      </ToolbarDropdown>

      <div className="w-px h-5 bg-gray-200 mx-1" />

      {/* ── Lists + indent/outdent ───────────────────────────────── */}
      <B active={editor.isActive('bulletList')} onClick={() => applyList(editor, 'toggleBulletList')} title="Bullet List">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2 4.75a1.25 1.25 0 112.5 0 1.25 1.25 0 01-2.5 0zm5-.75a.75.75 0 000 1.5h10.5a.75.75 0 000-1.5H7zm0 5a.75.75 0 000 1.5h10.5a.75.75 0 000-1.5H7zm0 5a.75.75 0 000 1.5h10.5a.75.75 0 000-1.5H7zM2 9.75a1.25 1.25 0 112.5 0 1.25 1.25 0 01-2.5 0zm0 5a1.25 1.25 0 112.5 0 1.25 1.25 0 01-2.5 0z" /></svg>
      </B>
      <B active={editor.isActive('orderedList')} onClick={() => applyList(editor, 'toggleOrderedList')} title="Numbered List">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M3.5 3a.5.5 0 01.5.5v2h.5a.5.5 0 010 1H2.5a.5.5 0 010-1H3V4h-.5a.5.5 0 010-1h1zM7 4a.75.75 0 000 1.5h10.5a.75.75 0 000-1.5H7zm0 5a.75.75 0 000 1.5h10.5a.75.75 0 000-1.5H7zm0 5a.75.75 0 000 1.5h10.5a.75.75 0 000-1.5H7zM2.5 9a.5.5 0 01.45.28l.6 1.2H4a.5.5 0 010 1H2.5a.5.5 0 01-.45-.72L3 9.5H2.5a.5.5 0 010-1z" /></svg>
      </B>
      <B
        onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
        disabled={!editor.can().sinkListItem('listItem')}
        title="Increase indent (Tab inside a list)"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4h14v2H3V4zm4 4h10v2H7V8zm-4 4h14v2H3v-2zm4 4h10v2H7v-2z" /><path d="M2 8.5L4.5 11 2 13.5z" /></svg>
      </B>
      <B
        onClick={() => editor.chain().focus().liftListItem('listItem').run()}
        disabled={!editor.can().liftListItem('listItem')}
        title="Decrease indent (Shift+Tab inside a list)"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4h14v2H3V4zm4 4h10v2H7V8zm-4 4h14v2H3v-2zm4 4h10v2H7v-2z" /><path d="M5 8.5L2.5 11 5 13.5z" /></svg>
      </B>
      <B
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        title="Block quote"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M6 5h2v6H6a2 2 0 00-2 2v0H2v-2a4 4 0 014-4V5zm8 0h2v6h-2a2 2 0 00-2 2v0h-2v-2a4 4 0 014-4V5z" /></svg>
      </B>
      <B
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal rule"
      >
        <span className="text-base leading-none">―</span>
      </B>

      <div className="w-px h-5 bg-gray-200 mx-1" />

      {/* ── Link / Image ─────────────────────────────────────────── */}
      <B
        active={editor.isActive('link')}
        onClick={() => {
          if (editor.isActive('link')) {
            editor.chain().focus().unsetLink().run();
            return;
          }
          setLinkPromptOpen(true);
        }}
        title="Insert link"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
      </B>
      <PromptDialog
        open={linkPromptOpen}
        title="Insert link"
        label="Paste or type the URL — opens in a new tab when clicked from the agreement."
        placeholder="https://example.com"
        onSubmit={(url) => {
          setLinkPromptOpen(false);
          if (url) editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
        }}
        onCancel={() => setLinkPromptOpen(false)}
      />
      {/* Image insertion. The handler is owned by the editor modal so it can
          access the selected mediator id (for storage scoping) and api
          credentials. Clicking opens a hidden file picker; on selection,
          handler uploads to /signing/templates/upload-image and inserts
          an <img> at the cursor with the returned URL. */}
      <B
        active={editor.isActive('image')}
        onClick={onInsertImage}
        title="Insert Image (logo or graphic)"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </B>

      {/* Image-only controls — visible when the cursor is on an image.
          Provides float (left/center/right/none) + size presets.
          Drag-resize via the corner handles in the editor still works
          regardless of these buttons; presets are quick alternatives. */}
      {editor.isActive('image') && (
        <>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <B
            active={editor.getAttributes('image').float === 'left'}
            onClick={() => editor.chain().focus().updateAttributes('image', { float: 'left' }).run()}
            title="Float Left (text wraps to the right)"
          >
            <span className="text-xs font-bold">⇤</span>
          </B>
          <B
            active={editor.getAttributes('image').float === 'center'}
            onClick={() => {
              // Centering only LOOKS centered when the image is narrower
              // than the document. Full-width images (max-width:100%) fill
              // the row, leaving nothing to center against. So when the
              // user clicks Center and no explicit width has been set, we
              // also default to Medium (240px) — they can resize after.
              // If the image already has a width (drag-resized or a
              // preset), keep that width as-is.
              const current = editor.getAttributes('image');
              const updates = { float: 'center' };
              const w = (current.width || '').trim();
              if (!w || w === '100%') {
                updates.width = '240px';
              }
              editor.chain().focus().updateAttributes('image', updates).run();
            }}
            title="Center (auto-sizes to Medium if image is full-width)"
          >
            <span className="text-xs font-bold">⇔</span>
          </B>
          <B
            active={editor.getAttributes('image').float === 'right'}
            onClick={() => editor.chain().focus().updateAttributes('image', { float: 'right' }).run()}
            title="Float Right (text wraps to the left)"
          >
            <span className="text-xs font-bold">⇥</span>
          </B>
          <B
            active={!editor.getAttributes('image').float || editor.getAttributes('image').float === 'none'}
            onClick={() => editor.chain().focus().updateAttributes('image', { float: 'none' }).run()}
            title="No float (inline)"
          >
            <span className="text-xs font-bold">⌷</span>
          </B>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          {IMAGE_WIDTH_PRESETS.map((p) => (
            <B
              key={p.value}
              active={editor.getAttributes('image').width === p.value}
              onClick={() => editor.chain().focus().updateAttributes('image', { width: p.value }).run()}
              title={p.title}
            >
              <span className="text-[10px] font-semibold">{p.label}</span>
            </B>
          ))}
          <div className="w-px h-5 bg-gray-200 mx-1" />
          {/* Delete the selected image. Backspace also works inside the
              editor (atom node default) but the explicit button is more
              discoverable when the user has positioned the image and
              wants it gone. */}
          <B
            active={false}
            onClick={() => editor.chain().focus().deleteSelection().run()}
            title="Delete image"
          >
            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V3a1 1 0 011-1h4a1 1 0 011 1v4" />
            </svg>
          </B>
        </>
      )}

      {/* Table controls — ALWAYS visible so the feature is
          discoverable. Insert Table drops a 3×3 grid at the cursor.
          Delete Table removes whatever table the cursor is inside;
          it's disabled (greyed out) when the cursor isn't in a
          table, so the button always shows but only works in
          context. Imports carry the source doc's tables, and this
          is how the CM gets rid of ones they don't want. */}
      <div className="w-px h-5 bg-gray-200 mx-1" />
      <B
        active={false}
        onClick={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        }
        title="Insert table"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
            d="M3 10h18M3 14h18M9 6v12M15 6v12M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
        </svg>
      </B>
      <button
        type="button"
        disabled={!editor.isActive('table')}
        onClick={() => editor.chain().focus().deleteTable().run()}
        title={editor.isActive('table') ? 'Delete this table' : 'Click inside a table first'}
        className={
          'h-8 px-2 inline-flex items-center gap-1 rounded text-xs font-medium border whitespace-nowrap shrink-0 transition-colors ' +
          (editor.isActive('table')
            ? 'text-red-600 border-red-200 bg-red-50 hover:bg-red-100'
            : 'text-gray-400 border-gray-200 bg-white cursor-not-allowed opacity-60')
        }
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V3a1 1 0 011-1h4a1 1 0 011 1v4" />
        </svg>
        Delete Table
      </button>
    </div>
  );
}

/* ── Editor Modal ────────────────────────────────────────────────────── */
// React dropdown for the mediator picker — replaces the native <select>
// so the menu styling stays consistent with the rest of the app and so
// dark mode / focus states are within our control. Click-outside closes.
function MediatorDropdown({ mediators, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = mediators.find((m) => (m.userId || m.id) === value);
  const label = selected?.name || 'Select a mediator…';
  const isPlaceholder = !selected;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full h-9 px-3 rounded-lg flex items-center justify-between text-[13px] text-left"
        style={{
          fontFamily: 'Inter',
          border: `1px solid ${open ? '#115e59' : '#e5e5e5'}`,
          backgroundColor: '#ffffff',
          color: isPlaceholder ? '#9ca3af' : '#0e1e20',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <path d="M3 4.5L6 7.5L9 4.5" stroke="#0e1e20" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            backgroundColor: '#ffffff',
            border: '1px solid #e5e5e5',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 60,
            fontFamily: 'Inter',
          }}
        >
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-[13px]"
            style={{
              color: !value ? '#115e59' : '#5b5a64',
              backgroundColor: !value ? '#e8fdf5' : 'transparent',
              fontWeight: !value ? 500 : 400,
            }}
            onMouseEnter={(e) => { if (value) e.currentTarget.style.backgroundColor = '#f5f5f5' }}
            onMouseLeave={(e) => { if (value) e.currentTarget.style.backgroundColor = 'transparent' }}
          >
            Select a mediator…
          </button>
          {mediators.map((m) => {
            const id = m.userId || m.id;
            const isActive = id === value;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => { onChange(id); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-[13px]"
                style={{
                  color: isActive ? '#115e59' : '#0e1e20',
                  backgroundColor: isActive ? '#e8fdf5' : 'transparent',
                  fontWeight: isActive ? 500 : 400,
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#f5f5f5' }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                {m.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TemplateEditorModal({ template, mediators, onClose, onSave }) {
  const [name, setName] = useState(template?.template_name || template?.name || '');
  const [mediatorId, setMediatorId] = useState(template?.mediator_id || '');
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [toast, setToast] = useState({ kind: null, message: null });
  const showToast = (kind, message) => setToast({ kind, message });
  const dismissToast = () => setToast({ kind: null, message: null });
  // Document-conversion in-flight + the original file name so we can show
  // "Loaded from FOO.docx — edit & place tags below, then click Save."
  const [convertingDoc, setConvertingDoc] = useState(false);
  const [convertedFileName, setConvertedFileName] = useState(null);
  const [convertError, setConvertError] = useState(null);
  const docInputRef = useRef(null);
  // Hidden <input type=file> the toolbar's Image button triggers via ref.
  // Keeping it outside the toolbar so the toolbar component stays a pure
  // presentation layer that just exposes an onInsertImage callback.
  const imageInputRef = useRef(null);

  // Initial editor content:
  //   - Edit existing template → load its saved html_content.
  //   - Create new template     → seed the editor with the starter boilerplate
  //                              so the CM doesn't stare at a blank page.
  const initialHtml = template?.html_content
    ? template.html_content
    : DEFAULT_AGREEMENT_TEMPLATE_HTML;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph', 'image'] }),
      // TextStyle is the foundation `<span style="...">` mark. Color +
      // FontSize + FontFamily + BackgroundColor + LineHeight ride on top
      // and round-trip those individual inline properties so DOCX run-
      // level formatting survives.
      TextStyle,
      Color,
      FontSize,
      FontFamily,
      BackgroundColor,
      // LineHeight defaults to `textStyle` mark (per-run). Word's line
      // spacing applies per-paragraph, so retarget the extension at
      // paragraph + heading nodes — `editor.chain().setLineHeight(v)`
      // then writes the attribute on the current paragraph instead of
      // a span.
      LineHeight.configure({ types: ['paragraph', 'heading'] }),
      // Whitelist line-height / padding-left / text-indent on paragraphs
      // + headings via a small global-attributes extension. (Run-level
      // properties are already covered by the upstream extensions
      // above, so we only need this one for paragraph-level styles.)
      InlineStyleExtension,
      // Table extensions — let DOCX-imported tables (e.g. signature row
      // grids) render as actual tables instead of vertical-stacked
      // paragraphs. resizable=false because the agreement layout is
      // fixed; the CM doesn't need to drag column widths.
      Table.configure({ resizable: false, HTMLAttributes: { class: 'agreement-table' } }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-teal-600 underline' } }),
      // Custom Image extension — inline images with float (left/right/
      // center) and width controls. allowBase64 must stay TRUE so DOCX
      // imports with embedded logos that fell back to data URLs still
      // render in the editor. The BE prefers Storage URLs but the
      // fallback path emits `data:image/...` and we need to accept it.
      CustomImage.configure({
        allowBase64: true,
      }),
      MergeTagNode,
    ],
    content: htmlToEditorContent(stripTrailingEmptyTables(initialHtml), MERGE_TAGS),
    editorProps: {
      attributes: {
        // Document-style canvas: 8.5"-feel inner page. Typography is calibrated
        // to match what the signing page renders to the party (see SigningPage.jsx
        // — the prose classes there use the same scales) so the editor IS the
        // preview. Padding mimics 1" margins, heading hierarchy mimics a Word/
        // Google Doc feel.
        class:
          'agreement-doc prose max-w-none focus:outline-none ' +
          'px-16 py-14 min-h-[760px] bg-white text-[#1f2937] leading-relaxed ' +
          '[&_h1]:text-[28px] [&_h1]:font-bold [&_h1]:text-center [&_h1]:mt-0 [&_h1]:mb-8 ' +
          '[&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:mt-7 [&_h2]:mb-3 ' +
          '[&_h3]:text-[16px] [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-2 ' +
          '[&_p]:my-3 [&_p]:text-[15px] [&_p]:leading-7 ' +
          // list-item is what actually guarantees the marker renders — a stray
          // display:block from imported Word markup silently hides it. Same
          // safeguard the email editor applies to .email-body-content li; the
          // sizing stays the agreement's own, since this document sets its own
          // type scale.
          '[&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1 [&_li]:text-[15px] [&_li]:leading-7 [&_li]:list-item ' +
          '[&_a]:text-teal-700 [&_a]:underline ' +
          // Table styling — imported tables (signature rows, etc.) get
          // a clean grid look that mirrors the public sign page's
          // signature table.
          '[&_table]:w-full [&_table]:my-4 [&_table]:border-collapse ' +
          '[&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-[13px] [&_th]:font-semibold ' +
          '[&_td]:border [&_td]:border-gray-300 [&_td]:px-3 [&_td]:py-2 [&_td]:text-[13px] [&_td]:align-top',
      },
    },
  });

  // Re-hydrate the editor whenever a different template is opened (Edit) so
  // the modal isn't stuck on the previous template's content. Same Create vs
  // Edit branching as initialHtml.
  useEffect(() => {
    if (!editor) return;
    const next = template?.html_content
      ? template.html_content
      : DEFAULT_AGREEMENT_TEMPLATE_HTML;
    editor.commands.setContent(htmlToEditorContent(stripTrailingEmptyTables(next), MERGE_TAGS));
  }, [editor, template?.id]);

  // Live-preview the picked mediator inside any {{Mediator Name}} pill
  // currently in the document.
  //
  //   • Picked  → pill shows the mediator's actual name AND switches to
  //               `resolved: true` so the green background drops, leaving
  //               plain inline text. Looks like the document does at sign
  //               time, just in the editor.
  //   • Cleared → pill shows "Mediator Name" again with the green chrome
  //               (resolved: false), so the CM can spot where the merge
  //               tag lives.
  //
  // The `tag` attr never changes — the saved template still has
  // {{mediator_name}} as the canonical token, substituted per case at
  // signing-request creation time.
  useEffect(() => {
    if (!editor) return;
    const picked = mediators.find((m) => (m.userId || m.id) === mediatorId);
    const label = picked?.name || 'Mediator Name';
    const resolved = !!picked;
    const isMediatorTag = (t) =>
      t === '{{mediator_name}}' || t === '{{mediatorName}}';

    editor.commands.command(({ tr, state }) => {
      let dirty = false;
      state.doc.descendants((node, pos) => {
        if (
          node.type.name === 'mergeTag' &&
          isMediatorTag(node.attrs.tag) &&
          (node.attrs.label !== label || node.attrs.resolved !== resolved)
        ) {
          tr.setNodeMarkup(pos, null, { ...node.attrs, label, resolved });
          dirty = true;
        }
      });
      return dirty;
    });
  }, [editor, mediatorId, mediators]);

  const insertMergeTag = (mt) => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertContent({ type: 'mergeTag', attrs: { tag: mt.tag, label: mt.label } })
      .run();
  };

  // Image insertion: opens the hidden <input type=file>, uploads the chosen
  // file to /signing/templates/upload-image, then inserts an <img> at the
  // editor's current cursor with the returned public URL.
  //
  // The actual <input change> handler is below; this wrapper is what the
  // toolbar's Image button calls. Kept as two functions so we don't have
  // to wire an event ref through the toolbar prop chain.
  const triggerImagePicker = () => {
    if (!editor || uploadingImage) return;
    if (imageInputRef.current) {
      // Reset value so picking the SAME file twice still fires onChange.
      imageInputRef.current.value = '';
      imageInputRef.current.click();
    }
  };

  const handleImageFileChosen = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Please choose an image file (PNG, JPEG, GIF, WebP, or SVG).');
      return;
    }
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append('file', file);
      // Pass mediatorId through so the BE can scope the storage path.
      // 'shared' fallback applies when the user hasn't picked a mediator
      // yet — uncommon but harmless.
      if (mediatorId) form.append('mediatorId', mediatorId);
      const resp = await api.post('/signing/templates/upload-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = resp.data?.data?.url;
      if (!url) throw new Error('Upload succeeded but no URL was returned.');
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      console.error('[template] image upload failed:', err);
      showToast('error', err.response?.data?.message || 'Image upload failed. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  // Document import: open hidden <input type=file>, send the picked DOCX/
  // DOC/PDF to /signing/templates/convert, then drop the returned HTML
  // into the live editor. No DB write — the CM still has to click Save.
  const triggerDocPicker = () => {
    if (convertingDoc) return;
    if (docInputRef.current) {
      docInputRef.current.value = '';
      docInputRef.current.click();
    }
  };

  const handleDocFileChosen = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;
    const okExt = /\.(docx|doc|pdf)$/i.test(file.name);
    if (!okExt) {
      setConvertError('Please choose a DOCX, DOC, or PDF file.');
      return;
    }
    setConvertingDoc(true);
    setConvertError(null);
    try {
      // Same simple BE path for DOCX, DOC, and PDF — mammoth/pdf-parse
      // give us text, headings, lists, and tables. Images get stripped
      // before content load (see prepImportedHtml) so we don't try to
      // capture or upload anything from the source document — the CM
      // can insert images directly via the toolbar if they need to.
      const form = new FormData();
      form.append('file', file);
      if (mediatorId) form.append('mediatorId', mediatorId);
      const resp = await api.post('/signing/templates/convert', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const html = resp.data?.data?.html;
      if (!html) throw new Error('Conversion produced no HTML.');
      editor.commands.setContent(htmlToEditorContent(html, MERGE_TAGS));
      setConvertedFileName(file.name);
      if (!name.trim()) {
        setName(file.name.replace(/\.(docx|doc|pdf)$/i, ''));
      }
    } catch (err) {
      console.error('[template] doc conversion failed:', err);
      setConvertError(err.response?.data?.message || err.message || 'Could not convert this file.');
    } finally {
      setConvertingDoc(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast('error', 'Please enter a template name.');
      return;
    }
    if (!mediatorId) {
      showToast('error', 'Please select a mediator.');
      return;
    }
    setSaving(true);
    try {
      const selectedMed = mediators.find((m) => m.userId === mediatorId || m.id === mediatorId);
      // Canonical content is now SyncFusion's SFDT JSON. Stored verbatim
      // in agreement_templates.html_content (the column name is legacy;
      // the field carries whichever wire format the editor emits).
      const htmlContent = editor
        ? editorContentToHtml(editor.getHTML(), MERGE_TAGS)
        : '';
      const payload = {
        templateName: name,
        mediatorId,
        mediatorName: selectedMed?.name || '',
        htmlContent,
      };
      if (template?.id) {
        await api.put(`/signing/templates/${template.id}`, payload);
      } else {
        await api.post('/signing/templates', payload);
      }
      onSave();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Failed to save template.';
      showToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
            {template?.id ? 'Edit Template' : 'Create Template'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4.5 4.5L13.5 13.5M4.5 13.5L13.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-4 sm:px-6 py-4 space-y-4">
          {/* Name + Mediator */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5b5a64] mb-1.5" style={{ fontFamily: 'Inter' }}>
                Template Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Standard Settlement Agreement"
                className="w-full h-9 px-3 rounded-lg border border-[#e5e5e5] bg-white text-[13px] text-[#0e1e20] placeholder-[#9ca3af] outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                style={{ fontFamily: 'Inter' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5b5a64] mb-1.5" style={{ fontFamily: 'Inter' }}>
                Mediator
              </label>
              <MediatorDropdown
                mediators={mediators}
                value={mediatorId}
                onChange={setMediatorId}
              />
            </div>
          </div>

          {/* Import row — converts a DOCX/DOC/PDF into the editor below.
              No DB write happens here; the CM still needs to click Save. */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#f9fafb] border border-gray-200 rounded-lg px-4 py-2.5">
            <div className="flex items-start sm:items-center gap-3">
              <svg className="w-5 h-5 text-[#115e59]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div>
                <div className="text-[12.5px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>
                  {convertedFileName
                    ? `Loaded from ${convertedFileName}`
                    : 'Start from a document'}
                </div>
                <div className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                  {convertError
                    ? <span className="text-red-600">{convertError}</span>
                    : <>Import a DOCX, DOC, or PDF — text, headings, lists, tables, and embedded images load into the editor for editing. <span className="text-[#94a3b8]">Word-specific styling (exact fonts, spacing, page layout) may need to be re-applied using the toolbar. PDFs come in as plain text.</span></>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={docInputRef}
                type="file"
                accept=".docx,.doc,.pdf"
                style={{ display: 'none' }}
                onChange={handleDocFileChosen}
              />
              <button
                onClick={triggerDocPicker}
                disabled={convertingDoc}
                className="h-8 px-4 rounded-lg border border-[#e5e5e5] text-[12px] font-medium text-[#0e1e20] bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap shrink-0"
                style={{ fontFamily: 'Inter' }}
              >
                {convertingDoc
                  ? 'Converting…'
                  : convertedFileName
                  ? 'Replace'
                  : 'Import Document'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[210px_1fr] gap-4">
            {/* Merge tag panel — sidebar on desktop, horizontal scroller on mobile */}
            <div className="border border-gray-200 rounded-lg p-3 self-start bg-white lg:space-y-1.5 lg:max-h-[640px] lg:overflow-auto">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 px-1 pb-1">
                Merge Tags
              </div>
              <p className="text-[11px] text-gray-400 px-1 pb-2 leading-relaxed">
                Click to insert. Real values are filled in per case when the document is sent.
              </p>
              <div className="flex gap-1.5 overflow-x-auto pb-1 lg:flex-col lg:overflow-x-visible lg:pb-0">
                {MERGE_TAGS.map((mt) => (
                  <button
                    key={mt.tag}
                    onClick={() => insertMergeTag(mt)}
                    className="shrink-0 lg:w-full lg:shrink text-left px-2 py-1.5 text-[12px] font-medium text-[#115e59] bg-[#f0fdfa] border border-[#ccfbf1] rounded-md hover:bg-[#ccfbf1] transition-colors whitespace-nowrap"
                    style={{ fontFamily: 'Inter' }}
                    title={mt.tag}
                  >
                    {mt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Editor — document-on-canvas layout */}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <Toolbar editor={editor} onInsertImage={triggerImagePicker} />
              {/* Hidden picker — opened by the toolbar's Image button. */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                style={{ display: 'none' }}
                onChange={handleImageFileChosen}
              />
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 bg-[#f0fdfa] border-b border-[#ccfbf1] text-[11px] text-[#115e59]" style={{ fontFamily: 'Inter' }}>
                <span>This is exactly what each party will see when signing.</span>
                <span className="text-[#5b5a64]">Letter format · 8.5 × 11″</span>
              </div>
              {/* Gray canvas with white "page" floating in middle */}
              <div className="relative bg-[#f3f4f6] py-4 sm:py-6 px-2 sm:px-4 max-h-[640px] overflow-y-auto">
                <div className="max-w-[820px] mx-auto bg-white shadow-md ring-1 ring-gray-200 rounded-sm">
                  <EditorContent editor={editor} />
                </div>
                {convertingDoc && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                    <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                      <div className="w-3 h-3 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                      <span className="text-[12px] text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>Converting document…</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="h-8 px-4 rounded-lg border border-[#e5e5e5] text-[13px] font-medium text-[#5b5a64] hover:bg-gray-50 transition-colors whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-8 px-4 rounded-lg bg-[#115e59] text-white text-[13px] font-medium hover:bg-[#0d4f4a] transition-colors disabled:opacity-50 whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>
      <Toast kind={toast.kind} message={toast.message} onDismiss={dismissToast} />
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────────── */
export default function TemplateManager() {
  const { user } = useAuth();
  const initials = (
    (user?.user_metadata?.first_name || user?.email?.[0] || 'U')[0] +
    ((user?.user_metadata?.last_name || '')[0] || '')
  ).toUpperCase();

  const [templates, setTemplates] = useState([]);
  const [mediators, setMediators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pageToast, setPageToast] = useState({ kind: null, message: null });
  const showPageToast = (kind, message) => setPageToast({ kind, message });
  const dismissPageToast = () => setPageToast({ kind: null, message: null });

  const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
    'templateManager.list',
    { name: 360, mediator: 220, created: 220 }
  );
  const { sortedRows: sortedTemplates, sort: tableSort, requestSort: requestTableSort } = useSortableData(
    templates,
    {
      name: (r) => r.template_name || r.name || '',
      mediator: (r) => r.mediator_name || r.mediatorName || '',
      created: (r) => r.created_at || '',
    }
  );

  const fetchTemplates = () => {
    setLoading(true);
    api
      .get('/signing/templates')
      .then((res) => setTemplates(res.data?.data || res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
    api
      .get('/mediators?limit=200')
      .then((res) => setMediators((res.data?.data || []).filter((m) => m.name?.trim())))
      .catch(console.error);
  }, []);

  const handleDelete = (templateId) => setConfirmDelete(templateId);
  const performDelete = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    if (!id) return;
    try {
      await api.delete(`/signing/templates/${id}`);
      fetchTemplates();
      showPageToast('success', 'Template deleted.');
    } catch (err) {
      console.error(err);
      showPageToast('error', 'Failed to delete template.');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowModal(true);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setEditingTemplate(null);
    fetchTemplates();
  };

  return (
    <div className="flex flex-col bg-white min-h-screen">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border-b border-[#e5e5e5] px-4 sm:px-6 py-3">
        <span className="text-sm font-semibold text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>
          Agreement Templates
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCreate}
            className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-[#115e59] text-white text-[13px] font-medium hover:bg-[#0d4f4a] transition-colors whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 2.333v9.334M2.333 7h9.334" stroke="currentColor" strokeWidth="1.167" strokeLinecap="round" />
            </svg>
            Upload Template
          </button>
          <div className="w-8 h-8 rounded-full bg-[#115e59] flex items-center justify-center shrink-0">
            <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Inter' }}>
              {initials}
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="rounded-xl bg-white overflow-x-auto border border-[#f0f0f0]">
          <div className="min-w-[720px]">
          {/* Table Header */}
          <div className="flex items-center bg-[#f9fafb] border-b border-[#f0f0f0]" style={{ height: 45 }}>
            <div className="flex items-center h-full px-4 relative min-w-0" style={{ width: colWidths.name }}>
              <SortableHeader sortKey="name" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Template Name</SortableHeader>
              <ResizeHandle onResize={setColWidth('name')} getCurrentWidth={() => colWidths.name} />
            </div>
            <div className="flex items-center h-full px-3 relative min-w-0" style={{ width: colWidths.mediator }}>
              <SortableHeader sortKey="mediator" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Mediator</SortableHeader>
              <ResizeHandle onResize={setColWidth('mediator')} getCurrentWidth={() => colWidths.mediator} />
            </div>
            <div className="flex items-center h-full px-3 relative min-w-0" style={{ width: colWidths.created }}>
              <SortableHeader sortKey="created" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Created</SortableHeader>
              <ResizeHandle onResize={setColWidth('created')} getCurrentWidth={() => colWidths.created} />
            </div>
            <div className="flex-1" />
            <div className="flex items-center justify-center h-full px-3" style={{ width: 120 }}>
              <span className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                Actions
              </span>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Loading...</span>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loading && templates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>No templates yet</span>
              <span className="text-xs text-gray-400 mt-1" style={{ fontFamily: 'Inter' }}>Upload your first agreement template to get started.</span>
              <button
                onClick={handleCreate}
                className="mt-4 flex items-center gap-1.5 h-8 px-4 rounded-lg bg-[#115e59] text-white text-[13px] font-medium hover:bg-[#0d4f4a] transition-colors"
                style={{ fontFamily: 'Inter' }}
              >
                Upload Template
              </button>
            </div>
          )}

          {/* Rows */}
          {!loading &&
            sortedTemplates.map((tpl) => (
              <div
                key={tpl.id}
                className="flex items-center border-b border-[#f0f0f0] hover:bg-gray-50 transition-colors"
                style={{ height: 48 }}
              >
                <div className="flex items-center h-full px-4 min-w-0" style={{ width: colWidths.name }}>
                  <span className="text-[13px] font-medium text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>
                    {tpl.template_name || tpl.name}
                  </span>
                </div>
                <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.mediator }}>
                  <span className="text-[13px] text-[#5b5a64] truncate" style={{ fontFamily: 'Inter' }}>
                    {tpl.mediator_name || tpl.mediatorName || '—'}
                  </span>
                </div>
                <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.created }}>
                  <span className="text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                    {tpl.created_at
                      ? new Date(tpl.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '-'}
                  </span>
                </div>
                <div className="flex-1" />
                <div className="flex items-center justify-center gap-1 h-full px-3" style={{ width: 120 }}>
                  <button
                    onClick={() => handleEdit(tpl)}
                    className="px-2.5 py-1 text-[11px] font-medium text-[#115e59] bg-[#f0fdfa] rounded hover:bg-[#ccfbf1] transition-colors"
                    style={{ fontFamily: 'Inter' }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(tpl.id)}
                    className="px-2.5 py-1 text-[11px] font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
                    style={{ fontFamily: 'Inter' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>{/* /min-w-[720px] */}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <TemplateEditorModal
          template={editingTemplate}
          mediators={mediators}
          onClose={() => {
            setShowModal(false);
            setEditingTemplate(null);
          }}
          onSave={handleModalSave}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this template?"
        body="This will remove the template from new cases. Existing signing requests already created against it remain intact."
        confirmLabel="Delete Template"
        destructive
        onConfirm={performDelete}
        onCancel={() => setConfirmDelete(null)}
      />
      <Toast kind={pageToast.kind} message={pageToast.message} onDismiss={dismissPageToast} />
    </div>
  );
}
